import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
// import PropTypes from 'prop-types';

export default function NameDetail() {
  const { biblicalName } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAllReferences, setShowAllReferences] = useState(false);

  /* ──────────────────────────── fetch JSON ───────────────────────────── */
  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        setError(false);

        const res = await fetch(
          `https://a0fb4023-namedbygod-worker.d-3bd.workers.dev/?q=${encodeURIComponent(
            biblicalName
          )}`
        );

        if (!res.ok) {
          throw new Error(`API responded with status: ${res.status}`);
        }

        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [biblicalName]);

  /* ─────────────────────── Set page title and meta ────────────────────── */
  useEffect(() => {
    if (data && data.title) {
      // Set page title
      document.title = data.title;

      // Set meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = data.description || '';
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Named by God';
    };
  }, [data]);

  // Helper function to convert text to Title Case
  const toTitleCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Function to clean content and convert newlines to paragraphs
  const cleanContent = (str) => {
    if (typeof str !== 'string') {
      return '';
    }

    // Remove quoted strings and trailing commas
    let cleaned = str;
    if (cleaned.startsWith('"')) {
      cleaned = cleaned.substring(1);
    }
    if (cleaned.endsWith('",')) {
      cleaned = cleaned.substring(0, cleaned.length - 2);
    } else if (cleaned.endsWith('"')) {
      cleaned = cleaned.substring(0, cleaned.length - 1);
    }

    // Replace escaped quotes with regular quotes
    cleaned = cleaned.replace(/\\"/g, '"');

    // Replace newlines with paragraph breaks
    if (cleaned.includes('\\n')) {
      // Split by newlines and wrap each part in paragraph tags
      return cleaned.split('\\n').filter(part => part.trim() !== '')
        .map(part => `<p>${part.trim()}</p>`).join('');
    }

    // If no newlines, just wrap the whole content in a paragraph
    return `<p>${cleaned}</p>`;
  };

  // Function to expand biblical references
  const expandBiblicalReference = (ref) => {
    if (!ref || !ref.startsWith('b.')) return ref;

    const parts = ref.split('.');
    if (parts.length < 4) return ref;

    const bookCode = parts[1];
    const chapter = parts[2];
    const verse = parts[3];

    const bookNames = {
      'GEN': 'Genesis',
      'EXO': 'Exodus',
      'LEV': 'Leviticus',
      'NUM': 'Numbers',
      'DEU': 'Deuteronomy',
      'JOS': 'Joshua',
      'JDG': 'Judges',
      'RUT': 'Ruth',
      '1SA': '1 Samuel',
      '2SA': '2 Samuel',
      '1KI': '1 Kings',
      '2KI': '2 Kings',
      '1CH': '1 Chronicles',
      '2CH': '2 Chronicles',
      'EZR': 'Ezra',
      'NEH': 'Nehemiah',
      'EST': 'Esther',
      'JOB': 'Job',
      'PSA': 'Psalm',
      'PRO': 'Proverbs',
      'ECC': 'Ecclesiastes',
      'SNG': 'Song of Solomon',
      'ISA': 'Isaiah',
      'JER': 'Jeremiah',
      'LAM': 'Lamentations',
      'EZK': 'Ezekiel',
      'DAN': 'Daniel',
      'HOS': 'Hosea',
      'JOL': 'Joel',
      'AMO': 'Amos',
      'OBA': 'Obadiah',
      'JON': 'Jonah',
      'MIC': 'Micah',
      'NAM': 'Nahum',
      'HAB': 'Habakkuk',
      'ZEP': 'Zephaniah',
      'HAG': 'Haggai',
      'ZEC': 'Zechariah',
      'MAL': 'Malachi',
      'MAT': 'Matthew',
      'MRK': 'Mark',
      'LUK': 'Luke',
      'JHN': 'John',
      'ACT': 'Acts',
      'ROM': 'Romans',
      '1CO': '1 Corinthians',
      '2CO': '2 Corinthians',
      'GAL': 'Galatians',
      'EPH': 'Ephesians',
      'PHP': 'Philippians',
      'COL': 'Colossians',
      '1TH': '1 Thessalonians',
      '2TH': '2 Thessalonians',
      '1TI': '1 Timothy',
      '2TI': '2 Timothy',
      'TIT': 'Titus',
      'PHM': 'Philemon',
      'HEB': 'Hebrews',
      'JAS': 'James',
      '1PE': '1 Peter',
      '2PE': '2 Peter',
      '1JN': '1 John',
      '2JN': '2 John',
      '3JN': '3 John',
      'JUD': 'Jude',
      'REV': 'Revelation'
    };

    const bookName = bookNames[bookCode] || bookCode;
    return `${bookName} ${chapter}:${verse}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <p className="text-gray-500">Loading …</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <p className="text-red-600">Failed to load data.</p>
      </div>
    );
  }

  // Extract all sections from the correct location in the JSON
  const { name, sections = {} } = data;

  // Process image path
  let imgSrc = null;
  if (sections.illustration && sections.illustration.image_path) {
    const path = sections.illustration.image_path;
    imgSrc = `/images/${path.replace('placeholder_images/', '').replace(/\.\w+$/, '.webp').toLowerCase()}`;
  }

  // Extract proximity data
  const proximity = sections.significance?.proximity || null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-gray-700">
      {/* Header and Name */}
      <h1 className="text-4xl font-bold text-center text-gray-800 mb-10">
        {toTitleCase(name || '')}
      </h1>

      {/* Main content with responsive layout */}
      <div className="lg:flex lg:gap-8">
        {/* Image - shows after meaning on mobile, left side on desktop */}
        <div className="lg:w-1/3 lg:order-1 order-2 mb-8 lg:mb-0">
          {imgSrc && (
            <div className="hidden lg:block sticky top-8">
              <img
                src={imgSrc}
                alt={sections.illustration?.caption || name}
                className="rounded-lg shadow-md w-full aspect-square object-cover"
                width="800"
                height="800"
              />
              {sections.illustration?.caption && (
                <p className="text-sm text-gray-500 mt-2 italic">
                  {sections.illustration.caption}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Text content */}
        <div className="lg:w-2/3 lg:order-2 order-1">
          {/* Meaning Section */}
          {sections.meaning && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                {sections.meaning.title || "Meaning and Origin"}
              </h2>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: cleanContent(sections.meaning.content)
                }}
              />
            </div>
          )}

          {/* Mobile image placement - after meaning */}
          {imgSrc && (
            <div className="lg:hidden mb-10">
              <img
                src={imgSrc}
                alt={sections.illustration?.caption || name}
                className="rounded-lg shadow-md w-full aspect-square object-cover"
                width="800"
                height="800"
              />
              {sections.illustration?.caption && (
                <p className="text-sm text-gray-500 mt-2 italic">
                  {sections.illustration.caption}
                </p>
              )}
            </div>
          )}

          {/* Popularity Analysis */}
          {sections.popularity?.content && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                {sections.popularity.title || "Popularity Analysis"}
              </h2>
              <div
                className="prose max-w-none mb-6"
                dangerouslySetInnerHTML={{
                  __html: cleanContent(sections.popularity.content)
                }}
              />

              {/* Popularity Table */}
              {sections.popularity?.data && sections.popularity.data.length > 0 && (
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Country
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variant
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rank
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sections.popularity.data.slice(0, 15).map((row, idx) => (
                        <tr key={`popularity-${idx}`} className={idx % 2 ? 'bg-gray-50' : ''}>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{row.country}</td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-indigo-600">
                            {toTitleCase(row.variant)}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{row.rank}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-6 py-2 bg-gray-50 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Showing top {Math.min(sections.popularity.data.length, 15)} of&nbsp;
                      {sections.popularity.country_count || sections.popularity.data.length} records
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Significance Section */}
          {sections.significance && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                {sections.significance.title || "Biblical and Cultural Significance"}
              </h2>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: cleanContent(sections.significance.content)
                }}
              />
            </div>
          )}

          {/* Biblical Context */}
          {sections.significance?.biblical_context && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                Biblical Context
              </h2>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: cleanContent(sections.significance.biblical_context)
                }}
              />
            </div>
          )}

          {/* Divine Proximity Metrics */}
          {proximity && (
            <div className="mb-10 bg-gray-50 p-6 rounded-lg shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                Divine Proximity Metrics
              </h2>
              <div className="space-y-8">
                {proximity.god_sna > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <p className="text-lg font-medium text-gray-700">God Proximity</p>
                      <p className="text-sm font-medium text-indigo-600">
                        SNA: {proximity.god_sna} | Percentile: {proximity.god_percentile}
                      </p>
                    </div>
                    <div className="h-4 w-full bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 rounded-full"
                        style={{ width: `${proximity.god_percentile}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {proximity.jesus_sna > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <p className="text-lg font-medium text-gray-700">Jesus Proximity</p>
                      <p className="text-sm font-medium text-indigo-600">
                        SNA: {proximity.jesus_sna} | Percentile: {proximity.jesus_percentile}
                      </p>
                    </div>
                    <div className="h-4 w-full bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 rounded-full"
                        style={{ width: `${proximity.jesus_percentile}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Biblical References */}
          {sections.references?.content && sections.references.content.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                {sections.references.title || "Biblical References"}
              </h2>
              <div className="bg-gray-50 rounded-lg p-6 shadow-sm">
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {sections.references.content
                    .slice(0, showAllReferences ? sections.references.content.length : 5)
                    .map((reference, index) => (
                      <li key={`reference-${index}`} className="text-indigo-600 hover:text-indigo-800">
                        {expandBiblicalReference(reference)}
                      </li>
                  ))}
                </ul>

                {sections.references.content.length > 5 && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowAllReferences(!showAllReferences)}
                      className="text-indigo-600 hover:text-indigo-800 font-medium text-sm underline focus:outline-none"
                    >
                      {showAllReferences
                        ? `Show fewer references (${sections.references.content.length - 5} less)`
                        : `Show all references (${sections.references.content.length - 5} more)`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Related Names */}
          {sections.related_names?.names && sections.related_names.names.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                {sections.related_names.title || "Related Biblical Names"}
              </h2>
              <div className="flex flex-wrap gap-2">
                {sections.related_names.names.map((relatedName, index) => (
                  <Link
                    key={`related-name-${index}`}
                    to={`/name/${relatedName.name.toLowerCase()}`}
                    className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full
                             hover:bg-blue-200 transition"
                  >
                    {toTitleCase(relatedName.name)}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}